<template>
  <div :class="['pricing-tariffs-features-slider', `pricing-tariffs-features-slider--${tariff}`]">
    <div class="swiper" ref="swiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide swiper-slide--pre">
          <picture itemprop="image">
            <template v-if="mdSkip">
              <source :media="`(min-width: ${breakpoints.mdMin})`" :srcset="`${imgPreSrc}/lg.svg`" />
            </template>
            <template v-else>
              <source :media="`(min-width: ${breakpoints.lgMin})`" :srcset="`${imgPreSrc}/lg.svg`" />
              <source :media="`(min-width: ${breakpoints.mdMin})`" :srcset="`${imgPreSrc}/md.svg`" />
            </template>
            <source :srcset="`${imgPreSrc}/sm.svg`" />
            <img :src="`${imgPreSrc}/lg.svg`" alt="" />
          </picture>
        </div>
        <template v-for="(feature, i) in features">
          <div class="swiper-slide" :key="`swiper-slide-${i++}`">
            <PricingTariffsFeature
              :key="`feature-${i}`"
              :md-skip="mdSkip"
              :tariff="tariff"
              :index="i"
              :feature="feature"
            ></PricingTariffsFeature>
          </div>
        </template>
      </div>
      <div role="button" class="swiper-button-prev">
        <img :src="imgPrevUrl" width="60" height="60" :alt="translations.slider.prev" />
      </div>
      <div role="button" class="swiper-button-next">
        <img :src="imgNextUrl" width="60" height="60" :alt="translations.slider.next" />
      </div>
      <div v-if="!realIndex" role="button" class="button button--icon swiper-button-custom" @click="swiperNext">
        <span v-html="translations.slider._pre" />
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="14" fill="none">
          <path stroke-width="2.5" d="m1.5 12.5 6-5.50071L1.5 1.5" />
        </svg>
      </div>
      <div class="swiper-pagination"></div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import { EffectFade, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([EffectFade, SwiperNavigation, SwiperPagination]);

import { BREAKPOINTS } from '~scripts/core/breakpoints';
import translations from './pricing-translations';
import PricingTariffsFeature from './PricingTariffsFeature.vue';
import imgPrev from './img/prev.svg';
import imgNext from './img/next.svg';

export default {
  name: 'PricingTariffsFeaturesSlider',
  components: { PricingTariffsFeature },
  props: {
    enabled: {
      type: Boolean,
      default: true,
    },
    mdSkip: {
      type: Boolean,
      default: false,
    },
    tariff: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    resetSlider: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      swiper: undefined,
      realIndex: 0,
    };
  },
  computed: {
    region() {
      return window.region;
    },
    lang() {
      return this.region === 'ai' ? 'en' : 'ru';
    },
    breakpoints() {
      return BREAKPOINTS;
    },
    mediaSm() {
      return window.matchMedia(`(max-width: ${this.breakpoints.smMax})`);
    },
    mediaMd() {
      return window.matchMedia(`(min-width: ${this.breakpoints.mdMin}) and (max-width: ${this.breakpoints.mdMax})`);
    },
    mediaLg() {
      return window.matchMedia(`(min-width: ${this.breakpoints.lgMin})`);
    },
    translations() {
      return translations[this.lang];
    },
    features() {
      return this.translations.tariffs[this.tariff];
    },
    swiperOptions() {
      return {
        sm: {
          effect: 'slide',
          enabled: true,
          loop: false,
          navigation: false,
          nested: false,
          pagination: {
            clickable: true,
            el: '.modal .swiper-pagination',
            type: 'bullets',
          },
          roundLengths: true,
          slidesPerView: 'auto',
          spaceBetween: 12,
          speed: 200,
        },
        md: {
          effect: 'slide',
          enabled: true,
          loop: false,
          navigation: false,
          nested: false,
          pagination: false,
          roundLengths: true,
          slidesPerView: 'auto',
          slidesOffsetBefore: 60,
          slidesOffsetAfter: 60,
          spaceBetween: 20,
          speed: 200,
        },
        lg: {
          effect: 'fade',
          enabled: this.enabled,
          loop: true,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          nested: true,
          pagination: {
            clickable: true,
            el: '.swiper-pagination',
            type: 'bullets',
          },
          roundLengths: true,
          slidesPerView: 1,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          spaceBetween: 0,
          speed: 100,
        },
      };
    },
    imgPreSrc() {
      return `/static/promo-static/landing-2021/src/images/pages/price/index/pricing-tariffs-features/${this.tariff}/_pre/${this.lang}`;
    },
    imgPrevUrl() {
      return imgPrev;
    },
    imgNextUrl() {
      return imgNext;
    },
  },
  mounted() {
    this.swiperInit();
    this.swiperToggle();
    this.modalObserver();

    this.mediaSm.addEventListener('change', this.mediaChange, { passive: true });
    this.mediaMd.addEventListener('change', this.mediaChange, { passive: true });
    this.mediaLg.addEventListener('change', this.mediaChange, { passive: true });
  },
  beforeDestroy() {
    this.swiperDestroy();

    this.mediaSm.removeEventListener('change', this.mediaChange);
    this.mediaMd.removeEventListener('change', this.mediaChange);
    this.mediaLg.removeEventListener('change', this.mediaChange);
  },
  watch: {
    resetSlider: function () {
      this.swiperReset();
    },
    activeIndex: function () {
      this.swiperToggle();
    },
  },
  methods: {
    mediaChange() {
      this.swiperDestroy();
      this.swiperInit();
    },
    swiperInit() {
      const media = this.mediaSm.matches ? 'sm' : this.mediaMd.matches ? (this.mdSkip ? 'lg' : 'md') : 'lg';
      this.swiper = new Swiper(this.$refs.swiper, this.swiperOptions[media]);

      this.swiper.on('realIndexChange', (swiper) => {
        setTimeout(() => {
          this.realIndex = swiper.realIndex;
        }, 0);
      });
    },
    swiperDestroy() {
      if (this.swiper) {
        this.swiper.destroy();
        this.swiper = undefined;
      }
    },
    swiperReset() {
      const { swiper } = this;

      if (swiper) {
        this.swiper.realIndex = 0;
        this.realIndex = 0;

        if (swiper.enabled) {
          swiper.slideToLoop(0, 0, false);
        }
      }
    },
    swiperToggle() {
      if (this.mediaLg.matches) {
        const { index, activeIndex } = this;
        const activeIndexNormalized = activeIndex + 1;

        if (index !== activeIndexNormalized) {
          if (this.swiper && this.swiper.enabled) {
            this.swiper.disable();
          }
        } else {
          if (this.swiper && !this.swiper.enabled) {
            this.swiper.enable();
          }
        }
      }
    },
    swiperNext() {
      this.swiper.slideNext();
    },
    modalObserver() {
      let modal = document.querySelectorAll('.modal');

      modal.forEach((modal) => {
        const config = {
          attributes: true,
        };

        const callback = () => {
          if (modal.className.includes('modal--open')) {
            this.swiperReset();
          }
        };

        if (modal.querySelector('.swiper')) {
          const observer = new MutationObserver(callback);
          observer.observe(modal, config);
        }
      });
    },
  },
};
</script>
